import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { MainLayout } from '../components/MainLayout';

export function WalletsPage() {
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    address: '',
    network: '',
    chain: '',
    label: '',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['wallets'],
    queryFn: () => api.wallets.list(),
  });

  const addMutation = useMutation({
    mutationFn: (data: typeof formData) => api.wallets.add(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
      setShowAddForm(false);
      setFormData({ address: '', network: '', chain: '', label: '' });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.wallets.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
    },
  });

  const wallets = data?.data?.wallets || [];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addMutation.mutate(formData);
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Wallet Addresses</h1>
            <p className="text-muted-foreground mt-1">Manage your crypto wallet addresses</p>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            {showAddForm ? 'Cancel' : 'Add Wallet'}
          </button>
        </div>

        {showAddForm && (
          <div className="p-6 bg-card rounded-lg border">
            <h2 className="text-xl font-semibold mb-4">Add New Wallet</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Wallet Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                  placeholder="0x..."
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Network</label>
                  <select
                    value={formData.network}
                    onChange={(e) => setFormData({ ...formData, network: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                    required
                  >
                    <option value="">Select network</option>
                    <option value="ERC20">ERC20</option>
                    <option value="TRC20">TRC20</option>
                    <option value="POLYGON">Polygon</option>
                    <option value="BASE">Base</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Chain</label>
                  <input
                    type="text"
                    value={formData.chain}
                    onChange={(e) => setFormData({ ...formData, chain: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                    placeholder="USDT, USDC, etc."
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Label (Optional)</label>
                <input
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                  placeholder="My wallet"
                />
              </div>
              {addMutation.isError && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
                  {(addMutation.error as any)?.response?.data?.error || 'Failed to add wallet'}
                </div>
              )}
              <button
                type="submit"
                disabled={addMutation.isPending}
                className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                {addMutation.isPending ? 'Adding...' : 'Add Wallet'}
              </button>
            </form>
          </div>
        )}

        <div className="bg-card rounded-lg border">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-muted-foreground">Loading...</div>
            </div>
          ) : wallets.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64">
              <div className="text-muted-foreground">No wallet addresses added yet</div>
              <button
                onClick={() => setShowAddForm(true)}
                className="mt-4 text-primary hover:underline"
              >
                Add your first wallet
              </button>
            </div>
          ) : (
            <div className="divide-y">
              {wallets.map((wallet: any) => (
                <div key={wallet.id} className="p-4 hover:bg-muted/20">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {wallet.label && (
                          <span className="font-medium">{wallet.label}</span>
                        )}
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          {wallet.network}
                        </span>
                        <span className="text-sm text-muted-foreground">{wallet.chain}</span>
                      </div>
                      <p className="font-mono text-sm text-muted-foreground mt-1 break-all">
                        {wallet.address}
                      </p>
                      {wallet.usageStats && (
                        <p className="text-sm text-muted-foreground mt-2">
                          Used in {wallet.usageStats.totalTransactions} transaction(s) •
                          Last used: {wallet.usageStats.lastUsed
                            ? new Date(wallet.usageStats.lastUsed).toLocaleDateString()
                            : 'Never'}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        if (confirm('Are you sure you want to remove this wallet?')) {
                          removeMutation.mutate(wallet.id);
                        }
                      }}
                      disabled={removeMutation.isPending}
                      className="ml-4 px-3 py-1 text-sm text-destructive hover:bg-destructive/10 rounded-md"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
